import { useLocation } from 'react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { UseDataProps } from './types';
import { useStore } from '30.quickConnect.Stores';
import { FormViewer } from '90.quickConnect.Models/models';
import { FEATURE_013 } from '30.quickConnect.Stores/RootStore/LoginStore/const';
import { normalizeFormId } from '30.quickConnect.Stores/RootStore/FormStore/favorites';

const useData = (): UseDataProps => {
  // On récupère le store
  const {
    UserSettingsStore: { formsAsTree },
    FormStore: { MainMenuStore, getFormsList, favoriteFormIds, synchronizeFavorites },
    CommonStore: { setWorkInProgress, setWorkDone },
    DeclarationStore: { setCloseConsentPopup },
    LoginStore: {
      signInInfos: { userUPN },
      currentOuCode,
      checkForFeature,
    },
  } = useStore();

  const location = useLocation();
  const { items: forms, filterThis: filterThisForms, filter } = MainMenuStore;

  const { t } = useTranslation(['declaration', 'forms']);

  const getFormsJob = useMemo(() => [uuidv4(), t('jobMessage_getFormsJob')] as [string, string], [t]);
  const [filteredForms, setFilteredForms] = useState<FormViewer[]>([]);

  const souldGetFormList = useRef(true);
  const lastSetOuCode = useRef<string | undefined>(undefined);

  const getDeclarationsJob = useMemo(
    () => [uuidv4(), t('declaration:jobMessage.getDeclarationsJob')] as [string, string],
    [t],
  );

  const isFavoritesFeatureEnabled = checkForFeature(FEATURE_013);

  useEffect(() => {
    setCloseConsentPopup();
    if (souldGetFormList.current === true || lastSetOuCode.current !== currentOuCode) {
      souldGetFormList.current = false;
      // on appelle getformviewer que dans l'ecran de la liste des formulaires
      if (location.pathname === '/forms') {
        setWorkInProgress(getFormsJob);
        getFormsList(t, isFavoritesFeatureEnabled).finally(() => {
          setWorkDone(getFormsJob[0]);
          lastSetOuCode.current = currentOuCode;
        });
      }
    }
  }, [
    currentOuCode,
    getDeclarationsJob,
    getFormsJob,
    getFormsList,
    isFavoritesFeatureEnabled,
    location.pathname,
    setCloseConsentPopup,
    setWorkDone,
    setWorkInProgress,
    t,
    userUPN,
  ]);

  // Retour sur l'onglet ou retour de la connexion : relecture des favoris (modifiés depuis un autre appareil ou un
  // autre onglet), et renvoi des changements faits hors connexion ou qu'un appel en échec n'aurait pas transmis
  useEffect(() => {
    if (!isFavoritesFeatureEnabled) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void synchronizeFavorites(true);
      }
    };
    const onOnline = () => {
      void synchronizeFavorites(true);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
    };
  }, [isFavoritesFeatureEnabled, synchronizeFavorites]);

  useEffect(() => {
    const newFilteredForms = filterThisForms(forms ?? [], (f: FormViewer) => f.name);
    setFilteredForms(newFilteredForms);
    // Ne pas enlever filter de ce tableau de dépendance !
  }, [filter, filterThisForms, forms]);

  const sortedFilteredForms = useMemo(() => {
    if (!isFavoritesFeatureEnabled) return filteredForms;

    // Favoris reconnus par l'identifiant du formulaire
    const favorites = new Set(favoriteFormIds);

    return [...filteredForms].sort((a, b) => {
      const aIsFav = favorites.has(normalizeFormId(a.id)) ? -1 : 0;
      const bIsFav = favorites.has(normalizeFormId(b.id)) ? -1 : 0;
      return aIsFav - bIsFav || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [filteredForms, favoriteFormIds, isFavoritesFeatureEnabled]);

  return { formsAsTree, filteredForms: sortedFilteredForms, MainMenuStore };
};

export default useData;
